import { ApolloQueryResult } from "@apollo/client";
import { Badge, Tab, Tabs } from "@mui/material";
import {
  DashboardTask,
  Exact,
  GetMyDashboardTasksQuery,
  TaskStatusEnum,
} from "graphql/generated/schema-types";
import { ChangeEvent } from "react";
import { DashboardTypeTabs } from "../shared/interfaces";
import { DashboardActions } from "../state/action";
import { useDashboardState } from "../state/hooks";
import { IDashboardState } from "../state/initial-state";

interface DashboardTabsProps {
  refetch: (
    variables?:
      | Partial<
          Exact<{
            [key: string]: never;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<GetMyDashboardTasksQuery>>;
  data: GetMyDashboardTasksQuery | undefined;
}

export const DashboardTabs = ({ refetch, data }: DashboardTabsProps) => {
  const {
    dashboardDispatch,
    dashboardState: { tabIndex },
  } = useDashboardState();

  const TabFilters = async (currentTab: number) => {
    const callDashboardDispatch = (payload: IDashboardState) =>
      dashboardDispatch({
        type: DashboardActions.UPDATE_DASHBOARD_TASKS,
        payload: payload,
      });

    switch (currentTab) {
      case DashboardTypeTabs.OverdueTasks:
        await refetch().then((data) => {
          callDashboardDispatch({
            dashboardTasks: data.data.myDashboardTasks
              ?.overdueTasks as DashboardTask[],
            queryVariables: "overdueTasks",
          });
        });
        break;
      case DashboardTypeTabs.MyTasks:
        await refetch().then((data) => {
          callDashboardDispatch({
            dashboardTasks: data.data.myDashboardTasks
              ?.myTasks as DashboardTask[],
            queryVariables: "myTasks",
          });
        });
        break;
      case DashboardTypeTabs.GroupTasks:
        await refetch().then((data) => {
          callDashboardDispatch({
            dashboardTasks: data.data.myDashboardTasks
              ?.groupTasks as DashboardTask[],
            queryVariables: "groupTasks",
          });
        });
        break;
      case DashboardTypeTabs.LaterThisWeekTasks:
        await refetch().then((data) => {
          callDashboardDispatch({
            dashboardTasks: data.data.myDashboardTasks
              ?.laterThisWeekTasks as DashboardTask[],
            queryVariables: "laterThisWeekTasks",
          });
        });
        break;
    }
  };

  const handleTabChange = (event: ChangeEvent<unknown>, tabIndex: number) => {
    TabFilters(tabIndex);
    dashboardDispatch({
      type: DashboardActions.UPDATE_TAB_INDEX,
      payload: {
        tabIndex,
      },
    });
  };
  const overdueExcludingCancelled = () => {
    const nonCancelledTasks = data?.myDashboardTasks?.overdueTasks?.filter(
      (task) => task?.status !== "CANCELLED",
    );
    return nonCancelledTasks?.length ?? 0;
  };

  return (
    <Tabs
      style={{ flex: "1 1 auto" }}
      variant="scrollable"
      value={tabIndex}
      onChange={handleTabChange}
      aria-label="simple tabs example"
      indicatorColor={"secondary"}
    >
      <Tab
        label={
          <Badge badgeContent={overdueExcludingCancelled()} color="primary">
            Overdue Tasks
          </Badge>
        }
      />
      <Tab
        label={
          <Badge
            badgeContent={
              data?.myDashboardTasks?.myTasks?.filter(
                (e) => e!.status === TaskStatusEnum.Scheduled,
              ).length ?? 0
            }
            color="secondary"
          >
            My Tasks
          </Badge>
        }
      />
      <Tab
        label={
          <Badge
            badgeContent={
              data?.myDashboardTasks?.groupTasks?.filter(
                (e) => e?.status === TaskStatusEnum.Scheduled,
              ).length ?? 0
            }
            color="secondary"
          >
            Group Tasks
          </Badge>
        }
      />
      <Tab
        label={
          <Badge
            badgeContent={
              data?.myDashboardTasks?.laterThisWeekTasks?.length ?? 0
            }
            color="secondary"
          >
            Later this Week
          </Badge>
        }
      />
    </Tabs>
  );
};
